import styles from "./pet-grid.module.css";
import PetFilter from "../Filter";
import PetCard from "./components/PetCard";
import { TableLoader } from "../../../../components/LoaderC";
import { TablePagination } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { buildFilterParams } from "../../petQuery";

const isWithin30Days = (createdAt) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const today = new Date();
  const diffInDays = (today - createdDate) / (1000 * 60 * 60 * 24);
  return diffInDays <= 30;
};

const PetGridView = ({
  isGridView,
  handleView,
  filterData,
  setFilterData,
  setAddModalOpen,
  setDeletePetModalOpen,
  setSelectedId,
  setEditData,
  setEditModalOpen,
  query,
  refreshKey,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [perPage, setPerPage] = useState(8);
  const [page, setPage] = useState(0);
  const [gridData, setGridData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const lastQueryKeyRef = useRef(null);
  const loadedKeyRef = useRef(null);

  const fetchPets = useCallback(async () => {
    const seq = ++requestSeq.current;

    const isFirstLoad = !hasLoadedOnceRef.current;
    if (isFirstLoad) setContentLoading(true);
    else setRefetching(true);

    const params = { ...buildFilterParams(query), page: page + 1, per_page: perPage === -1 ? "all" : perPage };

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/pets/grid", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const rows = response.data?.data || [];
      const meta = response.data?.meta || {};
      const total = meta?.total ?? 0;

      // The current page can become empty after a delete; move back to the last page that has data
      if (rows.length === 0 && page > 0 && total > 0) {
        setPage(Math.max(0, (meta?.last_page ?? 1) - 1));
        return;
      }

      setGridData(rows);
      setTotalCount(total);
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Pets] Failed to load pet grid", error);
      toast.error(error?.response?.data?.message || "Unable to load pets. Please try again.");

      // Allow the next activation of the grid view to try again
      loadedKeyRef.current = null;
      setLoadError(true);
      setGridData([]);
      setTotalCount(0);
    } finally {
      if (seq === requestSeq.current) {
        if (isFirstLoad) setContentLoading(false);
        else setRefetching(false);
      }
    }
  }, [getAccessTokenSilently, query, page, perPage]);

  // Load only while the grid view is visible, and only if search, filters, paging or data changed
  // since the last load (switching views back doesn't refetch). Filters are shared with the list
  // view, so a changed query first goes back to page 1, which then triggers a single request.
  const queryKey = JSON.stringify(query);
  const requestKey = JSON.stringify([queryKey, page, perPage, refreshKey]);
  useEffect(() => {
    if (!isGridView) return;

    const queryChanged = lastQueryKeyRef.current !== null && lastQueryKeyRef.current !== queryKey;
    lastQueryKeyRef.current = queryKey;
    if (queryChanged && page !== 0) {
      setPage(0);
      return;
    }

    if (loadedKeyRef.current === requestKey) return;
    loadedKeyRef.current = requestKey;
    fetchPets();
  }, [isGridView, queryKey, requestKey, page, fetchPets]);

  return (
    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          <div className="control-pane">
            <PetFilter
              isGridView={isGridView}
              handleView={handleView.bind(this)}
              filterData={filterData}
              setFilterData={setFilterData.bind(this)}
              setAddModalOpen={setAddModalOpen.bind(this)}
            />

            {contentLoading && (
              <div className="Loader TableLoader">
                <TableLoader />
              </div>
            )}

            {!contentLoading && (
              <div className={`${styles.Row}`}>
                <div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>
                  {gridData.map((item) => {
                    const showBadge = isWithin30Days(item.created_at);
                    return (
                      <PetCard
                        key={item?.id}
                        item={item}
                        showBadge={showBadge}
                        setDeletePetModalOpen={setDeletePetModalOpen.bind(this)}
                        setSelectedId={setSelectedId.bind(this)}
                        setEditData={setEditData.bind(this)}
                        setEditModalOpen={setEditModalOpen.bind(this)}
                      />
                    );
                  })}

                  {gridData.length > 0 && (
                    <div className="Pagination TablePaginationNew List">
                      <TablePagination
                        count={totalCount}
                        color="primary"
                        page={page}
                        rowsPerPage={perPage}
                        onPageChange={(e, v) => setPage(v)}
                        rowsPerPageOptions={[
                          { label: "4", value: 4 },
                          { label: "8", value: 8 },
                          { label: "16", value: 16 },
                          { label: "All", value: -1 },
                        ]}
                        onRowsPerPageChange={(e) => {
                          setPage(0);
                          setPerPage(e.target.value);
                        }}
                        labelRowsPerPage={"Pets per Page:"}
                      />
                    </div>
                  )}

                  {loadError && (
                    <div className={`${styles.NoDataMain}`}>
                      <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                      <p className={`${styles.NoDataTextSub}`}>we couldn't load the pets.</p>
                      <button type="button" className={`${styles.RetryBu}`} onClick={() => { loadedKeyRef.current = requestKey; fetchPets(); }}>Retry</button>
                    </div>
                  )}

                  {!loadError && gridData.length === 0 && (
                    <div className={`${styles.NoDataMain}`}>
                      <div className={`${styles.NoDataIMG}`}>
                        <img src="/static/img/pawprint.png" alt="No Data" />
                      </div>
                      <p className={`${styles.NoDataText}`}>Unfortunately!</p>
                      <p className={`${styles.NoDataTextSub}`}>
                        we couldn't find any pet at this time.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetGridView;
